/**
 * The scene's half of the voice: what is on screen right now, and for how long.
 *
 * `voice.ts` decides WHETHER somebody speaks; this decides how long the bubble stays.
 * The split is the same one `useScene` and `payoff.ts` already have — the deciding is
 * pure and testable under Node, the timers live in a hook — and it is why `voice.ts`
 * imports nothing from React and nothing from the DOM.
 *
 * Three overlays, three holds, all from `pace.ts`:
 *
 *   - the BUBBLE, `tauntHold`, fading in over `tauntIn` and out over `tauntOut`;
 *   - the EXPRESSION, `expressionHold` — which is longer than the bubble at Fast and
 *     shorter at Cinematic, so a face is not still smiling three seconds after the
 *     thing it smiled about;
 *   - the SHOWN CARDS, `showHold`.
 *
 * Every timer is cleared on the next shuffle, so nothing survives into a hand it has
 * nothing to do with, and every one is cleared on unmount.
 */
import { useEffect, useRef, useState } from "react";
import type { FlightBus } from "./flight";
import type { Durations } from "./pace";
import { BubbleGate, type Voice, type VoiceEvent } from "./voice";
import type { Expression } from "../art/roster";
import { tauntById } from "./lines";
import type { TauntItem } from "./lines";

export interface VoiceScene {
  /** Seat -> what its bubble is showing. */
  taunts: Record<number, TauntItem>;
  /** Seat -> the face it is pulling. */
  expressions: Record<number, Expression>;
  /** Seat -> the two cards it has turned face up, for the hold. */
  shown: Record<number, readonly number[]>;
}

const EMPTY: VoiceScene = { taunts: {}, expressions: {}, shown: {} };

export function useVoice(voice: Voice | null, bus: FlightBus, durations: Durations): VoiceScene {
  const [scene, setScene] = useState<VoiceScene>(EMPTY);
  const dur = useRef(durations);
  dur.current = durations;

  /* Every outstanding timer, so a shuffle can clear the lot. */
  const timers = useRef(new Set<number>());
  const clearAll = () => {
    for (const t of timers.current) window.clearTimeout(t);
    timers.current.clear();
  };
  const after = (ms: number, fn: () => void) => {
    const h = window.setTimeout(() => { timers.current.delete(h); fn(); }, ms);
    timers.current.add(h);
  };

  useEffect(() => () => clearAll(), []);

  /*
   * Phase 7 box 0(5): at most three bubbles up at once. The gate decides; this hook
   * owns the clock. Each bubble on screen carries a token, so a seat whose bubble was
   * replaced is not taken down early by the timer of the one it replaced.
   */
  const gate = useRef(new BubbleGate<TauntItem>(3, durations.tauntHold));
  gate.current.maxWait = durations.tauntHold;
  const token = useRef<Record<number, number>>({});
  const put = (seat: number, item: TauntItem) => {
    const d = dur.current;
    const mine = (token.current[seat] ?? 0) + 1;
    token.current[seat] = mine;
    setScene((s) => ({ ...s, taunts: { ...s.taunts, [seat]: item } }));
    after(d.tauntIn + d.tauntHold + d.tauntOut, () => {
      if (token.current[seat] !== mine) return;
      setScene((s) => {
        const taunts = { ...s.taunts };
        delete taunts[seat];
        return { ...s, taunts };
      });
      for (const next of gate.current.release(seat, performance.now())) put(next.seat, next.item);
    });
  };

  /* The steps: Voice reads them, and a shuffle wipes the screen. */
  useEffect(() => {
    if (!voice) return;
    return bus.subscribe((step, before) => {
      if (step.kind === "shuffle") { clearAll(); gate.current.clear(); token.current = {}; setScene(EMPTY); }
      voice.onStep(step, before);
      /*
       * The payoff moment, which is where the verdict and the expressions both hang.
       *
       * `useScene` reads the same `pause` to name the hand; this reads it to decide who
       * is pleased about it. One moment, two readers, no second timeline.
       */
      if (step.kind === "pause") voice.showdownFaces(before);
    });
  }, [voice, bus]);

  /* And the events themselves. */
  useEffect(() => {
    if (!voice) return;
    return voice.subscribe((e: VoiceEvent) => {
      const d = dur.current;
      if (e.t === "taunt") {
        const item = tauntById(e.id);
        if (!item) return;
        const now = gate.current.offer(e.seat, item, performance.now(), e.seat === voice.heroSeat);
        if (now) put(now.seat, now.item);
      } else if (e.t === "expression") {
        setScene((s) => ({ ...s, expressions: { ...s.expressions, [e.seat]: e.expression } }));
        after(d.expressionHold, () =>
          setScene((s) => {
            const expressions = { ...s.expressions };
            delete expressions[e.seat];
            return { ...s, expressions };
          }));
      } else {
        setScene((s) => ({ ...s, shown: { ...s.shown, [e.seat]: e.cards } }));
        after(d.showHold, () =>
          setScene((s) => {
            const shown = { ...s.shown };
            delete shown[e.seat];
            return { ...s, shown };
          }));
      }
    });
  }, [voice]);

  return scene;
}
